import React, { useCallback, useState } from 'react'
import { FilledButton } from '../../common/buttons/FilledButton'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { centsClient } from '../../../../api/cents/clients/cents-client'
import { Contract } from '../../../../types/contract'
import { DonationType } from '../../../../enums/donation-type'
import { translations } from '../../../../utils/translations/translations-manager'
import Loader from '../../common/Loader'
import { ContractCompletion } from './ContractCompletion'
import { ContractActivationPayment } from './payment/ContractActivationPayment'
import { DialogError } from '../../common/dialogs/DialogError'

export function ContractActivation({
  originalContract,
  success,
  complete,
}: {
  originalContract: Contract
  success: () => void
  complete: () => void
}): JSX.Element {
  const [state, setState] = useState<'contract_completion' | 'contract_save' | 'payment' | 'success'>('contract_completion')
  const [contract] = useState<Contract>({ ...originalContract, contractType: originalContract.contractType ?? DonationType.recurring })
  const [error, setError] = useState<'contract_save_error'>()

  const onContractCompletionSuccess = useCallback(async () => {
    if (contract.charityId !== undefined && contract._id !== undefined) {
      setState('contract_save')
      try {
        await centsClient.contracts.patchContract(contract.charityId, contract._id, {
          selectedDonationAmount: contract.selectedDonationAmount,
          donationAmount: contract.donationAmount,
          subscriptionRecurrence: contract.subscriptionRecurrence,
          contractType: contract.contractType,
        })
      } catch {
        setError('contract_save_error')
        return
      }
      setState('payment')
    }
  }, [contract])

  const onPaymentSuccess = useCallback(() => {
    setState('success')
    complete()
  }, [complete])

  if (error) {
    return (
      <DialogError
        message={translations.texts.contract_update_error.toString()}
        onRetry={() => {
          setState('contract_completion')
          setError(undefined)
        }}
        retryTitle={translations.texts.retry.toString()}
      />
    )
  }

  if (contract._id && contract.charityId) {
    if (state === 'contract_completion') {
      return <ContractCompletion contract={contract} success={onContractCompletionSuccess} />
    } else if (state === 'contract_save') {
      return <Loader className={'h-8 w-8'} />
    } else if (state === 'payment') {
      return <ContractActivationPayment contract={contract} success={onPaymentSuccess} />
    } else if (state === 'success') {
      return <Success success={success} />
    }
  }

  return <></>
}

export function Success({ success }: { success: () => void }): JSX.Element {
  return (
    <>
      <div className="flex flex-col justify-center items-center">
        <CheckCircleIcon className="mr-2 mb-2 w-12 h-12 stroke-emerald-500" />
        <p className="mb-5">{translations.texts.subscription_activation_success.toString()}</p>
      </div>
      <div className="flex justify-center">
        <FilledButton onClick={success} label={translations.texts.close.toString()} />
      </div>
    </>
  )
}
